import * as React from 'react';
import { type ElementType, type ReactNode } from 'react';
import clsx from 'clsx';
import styles from './Heading.module.css';

type SpanProps = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLSpanElement>,
  HTMLSpanElement
>;

export type HeadingVariant =
  | 'Hero'
  | 'HeroAlt'
  | 'H1'
  | 'H1Alt'
  | 'H2'
  | 'H3'
  | 'H4'
  | 'H5'
  | 'H6'
  | 'H7';

interface Props<T extends ElementType> extends Pick<SpanProps, 'className'> {
  as?: T;
  children: ReactNode;
  variant: HeadingVariant;
  testid?: string;
}

function getDOMElement(variant: HeadingVariant, component?: ElementType) {
  if (component) {
    return component;
  }

  switch (variant) {
    case 'Hero':
    case 'HeroAlt':
    case 'H1':
    case 'H1Alt':
      return 'h1';
    case 'H2':
      return 'h2';
    case 'H3':
      return 'h3';
    case 'H4':
      return 'h4';
    case 'H5':
      return 'h5';
    case 'H6':
    case 'H7':
      return 'h6';
  }
}

function getVariantStyles(variant: HeadingVariant) {
  switch (variant) {
    case 'Hero':
      return styles.hero;
    case 'HeroAlt':
      return styles['hero-alt'];
    case 'H1':
      return styles.h1;
    case 'H1Alt':
      return styles['h1-alt'];
    case 'H2':
      return styles.h2;
    case 'H3':
      return styles.h3;
    case 'H4':
      return styles.h4;
    case 'H5':
      return styles.h5;
    case 'H6':
      return styles.h6;
    case 'H7':
      return styles.h7;
  }
}

export function Heading<T extends ElementType>(props: Props<T>) {
  const { variant, children, testid, className, as } = props;

  const As = getDOMElement(variant, as);
  const variantStyles = getVariantStyles(variant);

  return (
    <As data-testid={testid} className={clsx(variantStyles, className)}>
      {children}
    </As>
  );
}
